'use client';

import { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { MdClose } from 'react-icons/md';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useCookieConsentStore } from '@/stores/cookie-consent.store';
import {
  cookieModalOverlayStyles,
  cookieModalContentStyles,
  cookieToggleRowStyles,
} from './CookieBanner.style';
import type { CookieCustomizationModalProps } from './CookieBanner.types';

export const CookieCustomizationModal = ({ open, onClose }: CookieCustomizationModalProps) => {
  const t = useTranslations('CookieConsent');
  const tCommon = useTranslations('Common');
  const { preferences, saveCustomPreferences } = useCookieConsentStore();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, open);

  const [analytics, setAnalytics] = useState(preferences.analytics);
  const [marketing, setMarketing] = useState(preferences.marketing);

  useEffect(() => {
    if (open) {
      setAnalytics(preferences.analytics);
      setMarketing(preferences.marketing);
    }
  }, [open, preferences.analytics, preferences.marketing]);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  const handleSave = () => {
    saveCustomPreferences({ analytics, marketing });
    onClose();
  };

  if (!open) return null;

  const titleId = 'cookie-customization-title';

  const content = (
    <div className={cookieModalOverlayStyles()} onClick={onClose} data-cy="cookie-customization-overlay">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cookieModalContentStyles()}
        onClick={(e) => e.stopPropagation()}
        data-cy="cookie-customization-modal"
      >
        <div className="tw:flex tw:items-center tw:justify-between tw:mb-large">
          <h2 id={titleId} className="tw:font-bold tw:text-font tw:text-20">
            {t('title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tCommon('close')}
            className="tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:text-gray-dark tw:hover:opacity-80 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary"
            data-cy="cookie-customization-close-button"
          >
            <MdClose className="tw:h-5 tw:w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Necessary — always on */}
        <div className={cookieToggleRowStyles()}>
          <div className="tw:flex-1 tw:min-w-0">
            <p className="tw:font-semibold tw:text-font tw:text-16">{t('necessaryTitle')}</p>
            <p className="tw:text-14 tw:text-gray-dark tw:mt-nano">{t('necessaryDescription')}</p>
          </div>
          <input
            type="checkbox"
            checked
            disabled
            aria-label={t('necessaryTitle')}
            className="tw:ml-base tw:shrink-0 tw:h-5 tw:w-5 tw:accent-primary"
            data-cy="cookie-customization-necessary-checkbox"
          />
        </div>

        {/* Analytics */}
        <div className={cookieToggleRowStyles()}>
          <div className="tw:flex-1 tw:min-w-0">
            <p className="tw:font-semibold tw:text-font tw:text-16">{t('analyticsTitle')}</p>
            <p className="tw:text-14 tw:text-gray-dark tw:mt-nano">{t('analyticsDescription')}</p>
          </div>
          <input
            type="checkbox"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
            aria-label={t('analyticsTitle')}
            className="tw:ml-base tw:shrink-0 tw:h-5 tw:w-5 tw:accent-primary"
            data-cy="cookie-customization-analytics-checkbox"
          />
        </div>

        {/* Marketing */}
        <div className={cookieToggleRowStyles()}>
          <div className="tw:flex-1 tw:min-w-0">
            <p className="tw:font-semibold tw:text-font tw:text-16">{t('marketingTitle')}</p>
            <p className="tw:text-14 tw:text-gray-dark tw:mt-nano">{t('marketingDescription')}</p>
          </div>
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            aria-label={t('marketingTitle')}
            className="tw:ml-base tw:shrink-0 tw:h-5 tw:w-5 tw:accent-primary"
            data-cy="cookie-customization-marketing-checkbox"
          />
        </div>

        <div className="tw:flex tw:gap-small tw:mt-large">
          <Button
            size={ButtonSize.large}
            variant={ButtonVariant.secondary}
            onClick={onClose}
            className="tw:flex-1"
            data-cy="cookie-customization-cancel-button"
          >
            {tCommon('cancel')}
          </Button>
          <Button
            size={ButtonSize.large}
            variant={ButtonVariant.primary}
            onClick={handleSave}
            className="tw:flex-1"
            data-cy="cookie-customization-save-button"
          >
            {t('savePreferences')}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
